import { useEffect, useState } from 'react';

// Hooks
import {
    ADD_HASHTAG,
    REMOVE_HASHTAG,
    useAlbumContext
} from '@/utils/hooks/context/useAlbumContext';
import useTranslation from '@/utils/hooks/translation/useTranslation';
import useIsGroup from '@/utils/hooks/user/useIsGroup';

import PropTypes from 'prop-types';

import Icon from '@/components/base/Icon';
// Components
import Label from '@/components/base/Label';
import InputActionTag from '@/components/composite/Form/Input/InputActionTag';
import SelectAlbumTags from '@/components/pages/albums/AlbumFunnelModal/components/SelectAlbumTags';

const HashtagsFilter = ({
    hashtags,
    showLabel = true,
    placeholder,
    hideTagsList = false,
    forceManualInput = false,
    className = ''
}) => {
    const { t } = useTranslation('content');
    const { albumInfo, dispatch } = useAlbumContext();
    const isGroup = useIsGroup();

    const [hashtagsOptions, setHashtagsOptions] = useState(null);

    useEffect(() => {
        if (hashtags && !hashtagsOptions) {
            setHashtagsOptions(
                hashtags?.map((hashtag) => {
                    return {
                        type: 'hashtag',
                        label: `#${hashtag}`,
                        value: hashtag
                    };
                })
            );
        }
    }, [hashtags]);

    const defaultPlaceholder = placeholder || t('modal.choose_hashtags');

    const renderField = () => {
        // Use manual input for groups or when explicitly forced
        if (isGroup || forceManualInput) {
            return (
                <InputActionTag
                    id="hashtags"
                    name="hashtags"
                    icon={showLabel ? null : 'hashtag'}
                    placeholder={defaultPlaceholder}
                    shouldClearSpace
                    additionalCheckOnEvent={(e) =>
                        e.target.value?.trim() !== '#' && e.target.value.length <= 51
                    }
                    triggerEventList={[' ', 'space']}
                    onAddItem={(value) => {
                        // Normalize: remove # and spaces
                        let normalized = value.trim().replace(/^#+/, '').replace(/\s+/g, '');
                        const itemFormatted = normalized ? `#${normalized}` : '';

                        if (
                            itemFormatted &&
                            !albumInfo?.criteria?.hashtags?.includes(itemFormatted)
                        ) {
                            dispatch({
                                type: ADD_HASHTAG,
                                payload: itemFormatted
                            });
                        }
                    }}
                    onDeleteItem={(item) => {
                        if (albumInfo?.criteria?.hashtags?.includes(item)) {
                            dispatch({
                                type: REMOVE_HASHTAG,
                                payload: item
                            });
                        }
                    }}
                    tagList={hideTagsList ? [] : albumInfo?.criteria?.hashtags || []}
                />
            );
        }

        return (
            <SelectAlbumTags
                className="rounded-md border border-icoGray-300 dark:border-icoDarkMode-beton"
                placeholder={defaultPlaceholder}
                idInput="hashtags-search"
                buttonStyle="whiteWithBorder"
                options={hashtagsOptions?.filter(
                    (item) => !albumInfo?.criteria?.hashtags?.includes(item?.value)
                )}
                selectedItems={
                    hashtagsOptions?.filter((item) =>
                        albumInfo?.criteria?.hashtags?.includes(item?.value)
                    ) ?? []
                }
                addItem={(item) => {
                    dispatch({
                        type: ADD_HASHTAG,
                        payload: item.value
                    });
                }}
                removeItem={(item) => {
                    dispatch({
                        type: REMOVE_HASHTAG,
                        payload: item?.value
                    });
                }}
            />
        );
    };

    if (albumInfo.criteria?.album_type === 'stories') {
        return null;
    }

    return (
        <div className={className}>
            {showLabel && (
                <Label className="flex items-center space-x-2.5 pb-2">
                    <Icon icon="hashtag" className="text-sm text-icoBlue" />
                    <span className="text-icoBlack dark:text-white">{t('modal.hashtags')}</span>
                </Label>
            )}

            {renderField()}
        </div>
    );
};

HashtagsFilter.propTypes = {
    hashtags: PropTypes.array,
    showLabel: PropTypes.bool,
    placeholder: PropTypes.string,
    hideTagsList: PropTypes.bool,
    forceManualInput: PropTypes.bool,
    className: PropTypes.string
};

export default HashtagsFilter;
